import React, { useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

// Components
import JobTag from './tag/JobTag'
import Icon from './icon/Icon'

// Styles
import { defaultColor, hsla } from '../styles/colors'
import { FontWeight } from '../common/models/typography'
import { mqFrom } from '../styles/mediaQueries'
import { shadows } from '../styles/effects'

// Types
import { AnyColor } from '../common/models/colors'
import { ICompanyMemberPure } from '../common/models/Company'

export enum AvatarSize {
  ExtraSmall = 'extra_small', // 24x24 on mobile, 36x36 on desktop
  Small = 'small', // 36x36 on mobile, 48x48 on desktop
  Medium = 'medium', // 48x48 on mobile, 56x56 on desktop
  Large = 'large', // 56x56 on mobile, 88x88 on desktop
  XLarge = 'xLarge', // 56x56 on mobile, 160x160 on desktop
}

interface IProps {
  // Note that if you don't supply a size, the Avatar will take up
  // 100% of its container, in both height & width
  size?: AvatarSize
  color?: AnyColor // Only for initials-based avatars
  imgSrc?: string // Note: image takes precedence over initials
  initials?: string
  user?: ICompanyMemberPure // Note: if a user object is passed, it will update the image and initials
  className?: string
  external?: boolean
}

export interface IMembers {
  name: string
  avatar?: string
  checked?: boolean
  external?: boolean
  pending?: boolean
  // Appended to the hover tooltip as "{name} · {statusLabel}" when set.
  statusLabel?: string
}

interface IGroupedAvatarsProps {
  className?: string
  members: IMembers[]
  maxLength?: number
  // Theme colour of the surface these avatars sit on. Used to mask the avatar
  // behind the dotted pending ring so it no longer shows through the gaps
  // between dots. Defaults to the white card surface.
  surfaceColor?: AnyColor
}

export const getInitials = (name: string, secondName?: string) => {
  let initials: string
  initials = name.charAt(0).toUpperCase()
  if (secondName) {
    initials = initials + secondName.charAt(0).toUpperCase()
  }
  return initials
}

export const GroupedAvatars: React.FC<IGroupedAvatarsProps> = ({
  members,
  maxLength = 3,
  className,
  surfaceColor = 'lightest',
}) => (
  <Avatars className={className}>
    {members.map((member, memberIndex) => {
      if (memberIndex < maxLength) {
        const initials = getInitials(member.name)
        const plusHover = members
          .slice(maxLength - 1)
          .reduce((init, mem, index) => {
            return `${init} ${mem.name}${mem.checked ? ' (✓)' : ''}${
              index < members.length - maxLength ? ',' : ''
            }`
          }, '')
        const showMember =
          memberIndex < maxLength - 1 || members.length < maxLength + 1
        return (
          <StyledJobTag
            anchored
            hoverText={
              showMember
                ? `${member.name}${
                    member.statusLabel ? ` · ${member.statusLabel}` : ''
                  }`
                : plusHover
            }
            key={`${initials}-${memberIndex}`}
            withMargin={memberIndex > 0}
            content={
              <>
                <SmallAvatar
                  checked={showMember && member.checked}
                  pending={showMember && member.pending}
                  $surfaceColor={surfaceColor}
                  external={member.external}
                  imgSrc={showMember ? member.avatar : undefined}
                  initials={
                    showMember ? initials : `+${members.length - maxLength + 1}`
                  }
                />
                {showMember && member.checked && (
                  <StyledCheckIcon type="Check" size={16} color="#fff" />
                )}
              </>
            }
          />
        )
      }
    })}
  </Avatars>
)

const Avatar: React.FC<IProps> = ({
  size,
  color,
  imgSrc,
  initials,
  user,
  className,
  external,
}) => {
  const theme = useContext(ThemeContext)
  if (user) {
    initials = getInitials(user.firstName, user.lastName)
    imgSrc = user.picture
  }

  if (!color) {
    color = imgSrc || user?.picture ? 'lightest' : defaultColor
  }

  return (
    <StyledAvatar size={size} color={color} className={className}>
      {imgSrc ? (
        <AvatarImage src={imgSrc} referrerPolicy="no-referrer" />
      ) : user?.isDeleted ? (
        <StyledIcon type="NoUser" size={16} color={theme.colors.lightest} />
      ) : external ? (
        <StyledIcon
          type="ExternPeople"
          size={16}
          color={theme.colors.textLight}
        />
      ) : initials ? (
        <AvatarInitials>{initials}</AvatarInitials>
      ) : null}
    </StyledAvatar>
  )
}

const avatarSizeStyles = (size: AvatarSize) => {
  let mobileSize: number
  let mobileFontSize: number
  let desktopSize: number
  let desktopFontSize: number
  switch (size) {
    case AvatarSize.XLarge:
      mobileSize = 88
      mobileFontSize = 24
      desktopSize = 160
      desktopFontSize = 40
      break
    case AvatarSize.Large:
      mobileSize = 56
      mobileFontSize = 18
      desktopSize = 88
      desktopFontSize = 32
      break
    case AvatarSize.Medium:
      mobileSize = 48
      mobileFontSize = 16
      desktopSize = 56
      desktopFontSize = 18
      break
    case AvatarSize.Small:
      mobileSize = 36
      mobileFontSize = 14
      desktopSize = 48
      desktopFontSize = 16
      break
    case AvatarSize.ExtraSmall:
    default:
      mobileSize = 24
      mobileFontSize = 10
      desktopSize = 36
      desktopFontSize = 14
  }
  return css`
    width: ${mobileSize}px;
    height: ${mobileSize}px;
    font-size: ${mobileFontSize}px;

    ${mqFrom.M`
      width: ${desktopSize}px;
    `}

    ${mqFrom.M`
      height: ${desktopSize}px;
    `}

    ${mqFrom.M`
      font-size: ${desktopFontSize}px;
    `}
  `
}

const StyledIcon = styled(Icon)`
  align-self: center;
  margin-left: auto;

  &:last-child {
    margin-right: auto;
  }
`

export const StyledAvatar = styled.figure<{
  color: AnyColor
  size?: AvatarSize
}>`
  position: relative;
  display: flex;
  margin: 0;
  ${(props) =>
    props.size
      ? css`
          ${avatarSizeStyles(props.size)}
        `
      : css`
          width: 100%;
          height: 100%;

          ${AvatarInitials} {
            font-size: 12px;

            ${mqFrom.L`
              font-size: 14px;
            `}
          }
        `}
  border-radius: 50%;
  background-color: ${({ color, theme }) => theme.colors[color]};
`

export const AvatarInitials = styled.span`
  position: absolute;
  color: ${({ theme }) => theme.colors.lightest};
  font-weight: ${FontWeight.Medium};
  letter-spacing: 0.8px;
  align-self: center;
  text-align: center;
  width: 100%;
`

export const AvatarImage = styled.img`
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`

const Avatars = styled.div`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.lightest};
  box-shadow: ${shadows.depthTwo};
  margin-left: auto;
  border-radius: 100px;
`

const SmallAvatar = styled(Avatar)<{
  checked?: boolean
  pending?: boolean
  $surfaceColor?: AnyColor
}>`
  width: 28px;
  height: 28px;
  border: 2px solid ${({ theme }) => theme.colors.lightest};
  background-color: ${({ theme }) => theme.colors.primaryLight};
  position: relative;

  > span {
    color: ${({ theme }) => theme.colors.text};
  }

  ${({ checked, theme }) =>
    checked &&
    `
    &:after {
      content: '';
      position: absolute;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background-color: ${hsla(theme.colors.primary, 0.6)};
    }
  `}

  ${({ pending, $surfaceColor = 'lightest', theme }) =>
    pending &&
    css`
      border-style: dotted;
      border-color: ${theme.colors.textLight};

      /* Solid ring in the surface colour sits above the avatar but below the
         dotted border, masking the avatar through the gaps between dots. */
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        border: 2px solid ${theme.colors[$surfaceColor]};
        pointer-events: none;
      }
    `}
`

const StyledJobTag = styled(JobTag)<{ withMargin?: boolean }>`
  padding: 0;
  background: transparent;
  overflow: initial;
  cursor: pointer;
  margin-left: ${({ withMargin }) => withMargin && `-10px`};
  position: relative;
`

const StyledCheckIcon = styled(Icon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

export const StyledGroupedAvatars = styled(GroupedAvatars)`
  box-shadow: none;
  background: transparent;
  margin-right: 12px;

  strong {
    border-radius: 50%;
  }

  figure {
    width: 36px;
    height: 36px;
    box-shadow: ${shadows.depthTwo};
  }
`

export default Avatar
